import React, { useEffect, useState } from "react";
import { Tabs, Input, Button, Upload, Modal, Radio, Tooltip } from "antd";
import ImgCrop from "antd-img-crop";
import {
  EditOutlined,
  CloseOutlined,
  CloudFilled,
  CloseCircleFilled,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import useDebounce from "../../../layout/WelcomePage/Worksheets/useDebounce";
import {
  setComponentBackground,
  setBannerTextColor,
  setCustomPaletteInfo,
  setUpdatedAccentColors,
  setColorPickerId,
  togglePersonalisation,
} from "../../../../redux/actions/DIYPersonalization/PersonalizationSettings";
import {
  componentMapping,
  updateVariableValueBasedOnThemeOrRoot,
} from "../../../../features/DIYPersonalization/PersonalizationPreview/PersonalizationMapping";
import "./CustomColorPicker.module.css";
import { useSelector } from "react-redux";
import { callNotification, deepCopy } from "../../../../redux/constants";
import EmptyImage from "../../../../assets/emptyImage.png";
import { getUploadedImageUrl } from "../../../../redux/actions/DIYPersonalization/DIYPersonalizationAPI";
import { restrictedColors } from "../../../../redux/constants/enums";
import defaultImage from "../../../../assets/defaultImage.png";

const ColorPicker = ({
  index,
  keyComponent,
  configType,
  defaultValue,
  AdvancedPicker,
  onDelete,
  updateAccentList,
  accentLocalList,
  isColorPickerDisabled,
}) => {
  const dispatch = useDispatch();
  const PersonalizationSettingObj = useSelector(
    (state) => state?.PersonalizationSetting
  );

  // defining default values
  let defaultGradientStart, defaultGradientEnd, defaultSolidColor, imgUrl;
  // setting default value
  if (defaultValue) {
    if (defaultValue?.includes("linear-gradient")) {
      const hexRegex = /#(?:[0-9a-fA-F]{3}){1,2}/g; // Matches 3 or 6 digit hex colors
      const hexMatches = defaultValue.match(hexRegex);
      if (hexMatches && hexMatches.length >= 2) {
        [defaultGradientStart, defaultGradientEnd] = hexMatches;
      } else {
        console.error(
          "Could not extract both hex values from defaultValue:",
          defaultValue
        );
      }
    } else if (
      defaultValue?.includes("http") ||
      defaultValue?.includes("https")
    ) {
      imgUrl = defaultValue;
    } else {
      defaultSolidColor = defaultValue;
    }
  }

  // need to set a deafult active tab based on what is selected for banner background
  const [activeTab, setActiveTab] = useState(
    defaultGradientStart ? "gradient" : imgUrl ? "image" : "solid"
  );
  const [solidColor, setSolidColor] = useState(defaultSolidColor);
  const [gradientStart, setGradientStart] = useState(defaultGradientStart);
  const [gradientEnd, setGradientEnd] = useState(defaultGradientEnd);
  const [croppedImage, setCroppedImage] = useState(imgUrl || null);
  const [selectedAccentData, setSelectedAccentData] = useState(
    configType?.slice(0, -1) === "AccentColor" ? defaultSolidColor : null
  );

  // Initialize lastAppliedBackgrounds
  const [lastAppliedBackgrounds, setLastAppliedBackgrounds] = useState({
    solid: defaultSolidColor || null,
    gradient:
      defaultGradientStart && defaultGradientEnd
        ? [defaultGradientStart, defaultGradientEnd]
        : null,
    image: imgUrl || null,
  });

  const mapping = componentMapping[keyComponent];
  const [isImageValid, setImageValid] = useState(true);
  useEffect(() => {
    setSolidColor(defaultSolidColor);
    setGradientStart(defaultGradientStart);
    setGradientEnd(defaultGradientEnd);
    setCroppedImage(imgUrl);

    // For accent colors, initialize selectedAccentData with the default value
    if (configType?.slice(0, -1) === "AccentColor") {
      setSelectedAccentData(defaultSolidColor);
    }

    setLastAppliedBackgrounds((prev) => ({
      solid: defaultSolidColor || prev?.solid,
      gradient:
        defaultGradientStart && defaultGradientEnd
          ? [defaultGradientStart, defaultGradientEnd]
          : prev?.gradient,
      image: imgUrl || prev?.image,
    }));
  }, [
    defaultSolidColor,
    defaultGradientStart,
    defaultGradientEnd,
    imgUrl,
    configType,
  ]);

  // to ensure no 2 color pickers are open at once
  const openedColorPickerId = PersonalizationSettingObj?.openedColorPickerId;
  const isOpen =
    openedColorPickerId == `${index ? index : "-"}-${keyComponent}`;
  const toggleColorPicker = () => {
    // Reset selectedAccentData if we're closing an accent color picker
    if (isOpen && configType?.slice(0, -1) === "AccentColor") {
      // Reset to default value when closing without applying
      setSelectedAccentData(defaultSolidColor);
      setSolidColor(defaultSolidColor);
    }

    dispatch(
      setColorPickerId(isOpen ? null : `${index ? index : "-"}-${keyComponent}`)
    );
  };

  const { cssVariable, stateKey } = mapping;
  let hasData =
    solidColor ||
    croppedImage ||
    gradientStart ||
    lastAppliedBackgrounds?.solid ||
    lastAppliedBackgrounds?.image ||
    (lastAppliedBackgrounds?.gradient &&
      lastAppliedBackgrounds?.gradient?.length === 2);

  //helper functions
  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.readAsDataURL(file);
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  // runs when cropped image changes to maintain syntax of url of image
  useEffect(() => {
    if (!croppedImage) return;

    // Normalize the URL format
    const normalized =
      croppedImage.startsWith("url(") && croppedImage.endsWith(")")
        ? croppedImage.slice(4, -1).replace(/^["']|["']$/g, "")
        : croppedImage;

    if (normalized !== croppedImage) {
      setCroppedImage(normalized);
    }
  }, [croppedImage]);

  const handleColorChange = (color) => {
    // we need to check if not restricted color (debouncing would be better)
    if (restrictedColors.includes(color?.toLowerCase())) {
      callNotification(
        "This is a restricted color, please select other color",
        "error",
        40,
        "personalization-preview-notification personalization-preview-mask",
        true
      );
      return;
    }

    // If this is an accent color picker, just update the selectedAccentData
    if (configType?.slice(0, -1) === "AccentColor") {
      setSelectedAccentData(color);
      setSolidColor(color);
    } else {
      // For non-accent colors, update solidColor and lastAppliedBackgrounds
      setSolidColor(color);
      setLastAppliedBackgrounds((prev) => ({
        ...prev,
        solid: color,
      }));
    }
  };
  const handleCroppedImageUpload = async (selectedFile) => {
    if (!isImageValid) {
      setImageValid(true);
      return;
    }
    if (
      ["image/png", "image/jpeg", "image/jpg"]?.includes(selectedFile?.type)
    ) {
      const minSize = 5 * 1024; // 5KB
      const maxSize = 1024 * 1024 * 50; // 50MB
      // if (selectedFile?.size < minSize || selectedFile?.size > maxSize) {
      //   callNotification(
      //     "File size exceeds the allowed limit (5KB - 50MB)",
      //     "error",
      //     40,
      //     "personalization-preview-notification personalization-preview-mask",
      //     true
      //   );
      //   return;
      // }
      const url = URL.createObjectURL(selectedFile);
      setCroppedImage(url);
      setLastAppliedBackgrounds((prev) => ({
        ...prev,
        image: url,
      }));
      if (activeTab === "image") {
        handleColorPickerOutput(url, "Image", configType);
      }
      // Check file size (in bytes)
      // const minSize = 1 * 1024; // 1KB
      // const maxSize = 1024 * 1024 * 5; // 5MB
      const base64 = await convertBase64(selectedFile);
      dispatch(getUploadedImageUrl(base64, "banner"));
      // setLogoPreviewUrl(base64);
    } else {
      callNotification(
        "Unsupported file type! Please use PNG, JPG, or JPEG formats.",
        "error",
        40,
        "personalization-preview-notification personalization-preview-mask",
        true
      );
    }
  };

  // to update solid color in state with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      // Skip this for accent colors as they need explicit Apply button click
      if (configType?.slice(0, -1) === "AccentColor") {
        return;
      }

      if (solidColor) {
        // Always update lastAppliedBackgrounds
        setLastAppliedBackgrounds((prev) => ({
          ...prev,
          solid: solidColor,
        }));
        // Only apply to banner if on solid tab
        if (activeTab === "solid") {
          handleColorPickerOutput(solidColor, "SolidColor", configType);
        }
      }
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [solidColor, configType, activeTab]);

  // to update gradient in state with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (gradientStart && gradientEnd) {
        setLastAppliedBackgrounds((prev) => ({
          ...prev,
          gradient: [gradientStart, gradientEnd],
        }));

        // Only apply to banner if on gradient tab
        if (activeTab === "gradient") {
          handleColorPickerOutput(
            [gradientStart, gradientEnd],
            "GradientColor",
            configType
          );
        }
      }
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [gradientStart, gradientEnd, configType, activeTab]);

  // to update banner background based on active tab
  useEffect(() => {
    // Skip this for accent colors as they need explicit Apply button click
    if (configType?.slice(0, -1) === "AccentColor") {
      return;
    }

    if (activeTab === "image") {
      const imageToApply = croppedImage || lastAppliedBackgrounds?.image;
      if (imageToApply) {
        handleColorPickerOutput(imageToApply, "Image", configType);
      }
    } else if (activeTab === "gradient") {
      // First try local state
      if (gradientStart && gradientEnd) {
        handleColorPickerOutput(
          [gradientStart, gradientEnd],
          "GradientColor",
          configType
        );
      }
      // Fallback to lastAppliedBackgrounds
      else if (
        lastAppliedBackgrounds?.gradient &&
        lastAppliedBackgrounds?.gradient?.length === 2
      ) {
        handleColorPickerOutput(
          lastAppliedBackgrounds?.gradient,
          "GradientColor",
          configType
        );
        // Also update the local state from lastAppliedBackgrounds
        setGradientStart(lastAppliedBackgrounds?.gradient?.[0]);
        setGradientEnd(lastAppliedBackgrounds?.gradient?.[1]);
      }
    } else if (activeTab === "solid") {
      // First try local state, then fallback to lastAppliedBackgrounds
      const colorToApply = solidColor || lastAppliedBackgrounds?.solid;
      if (colorToApply) {
        handleColorPickerOutput(colorToApply, "SolidColor", configType);
        // Also update the local state if we're using the fallback
        if (!solidColor && lastAppliedBackgrounds?.solid) {
          setSolidColor(lastAppliedBackgrounds?.solid);
        }
      }
    }
  }, [activeTab]);

  const handleUpdateAccentData = () => {
    // Make sure we have selectedAccentData before proceeding
    if (!selectedAccentData) {
      return;
    }

    // Extract the actual accent index correctly
    if (configType?.slice(0, -1) === "AccentColor") {
      const accentIndex = configType.slice(-1);

      // Update accent array directly
      let accentArray = [...accentLocalList]; // Create a copy to avoid mutation
      accentArray[accentIndex] = selectedAccentData;

      // Call the update function passed from parent
      updateAccentList(accentArray);

      // Toggle personalization to refresh UI
      dispatch(
        togglePersonalisation(!PersonalizationSettingObj?.togglePersonalisation)
      );

      // Close the color picker after applying
      dispatch(setColorPickerId(null));
    }
    // else {
    //   console.error("Invalid configType for accent color update:", configType);
    // }
  };

  // HTML for color picker
  const getHtmlOfColorPicker = (tabKey = "") => {
    if (tabKey?.toLowerCase() === "solid") {
      return (
        <div className="PickerTabContent">
          <div className="PickerTabContent--label Setting__LabelFont--2 mb-1">
            Click below to access color picker
          </div>
          <Input
            type="color"
            value={solidColor || lastAppliedBackgrounds?.solid || ""}
            onChange={(e) => handleColorChange(e.target.value)}
            className="PickerTabContent--colorInput mb-2"
          />
          {configType?.slice(0, -1) === "AccentColor" ? (
            <button
              style={{
                minWidth: "100%",
                borderRadius: "1.25rem",
                padding: "0.25rem",
              }}
              className="ant-btn ant-btn-primary ant-btn-primary_blue"
              onClick={handleUpdateAccentData}
            >
              Apply
            </button>
          ) : (
            ""
          )}
        </div>
      );
    } else if (tabKey?.toLowerCase() === "gradient") {
      return (
        <div className="PickerTabContent">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <div className="PickerTabContent--label Setting__LabelFont--2">
              Start Color
            </div>
            <div className="PickerTabContent--label Setting__LabelFont--3">
              Click below to access color picker
            </div>
          </div>
          <Input
            type="color"
            value={
              gradientStart ||
              (lastAppliedBackgrounds?.gradient &&
                lastAppliedBackgrounds?.gradient?.[0]) ||
              ""
            }
            onChange={(e) => {
              setGradientStart(e.target.value);
              // Update lastAppliedBackgrounds when gradient start changes
              setLastAppliedBackgrounds((prev) => ({
                ...prev,
                gradient: [
                  e.target.value,
                  gradientEnd || (prev?.gradient && prev?.gradient?.[1]) || "",
                ],
              }));
            }}
            className="PickerTabContent--colorInput mb-2"
          />
          <div className="d-flex align-items-center justify-content-between mb-1">
            <div className="PickerTabContent--label Setting__LabelFont--2">
              End Color
            </div>
            <div className="PickerTabContent--label Setting__LabelFont--3">
              Click below to access color picker
            </div>
          </div>
          <Input
            type="color"
            value={
              gradientEnd ||
              (lastAppliedBackgrounds?.gradient &&
                lastAppliedBackgrounds?.gradient?.[1]) ||
              ""
            }
            onChange={(e) => {
              setGradientEnd(e.target.value);
              // Update lastAppliedBackgrounds when gradient end changes
              setLastAppliedBackgrounds((prev) => ({
                ...prev,
                gradient: [
                  gradientStart ||
                    (prev?.gradient && prev?.gradient?.[0]) ||
                    "",
                  e.target.value,
                ],
              }));
            }}
            className="PickerTabContent--colorInput mb-2"
          />
          <div className="PickerTabContent--label Setting__LabelFont--2 mb-1">
            Final Gradient
          </div>
          <div
            className="PickerTabContent--colorInputPreview mb-2"
            style={{
              background: `linear-gradient(to right, ${
                gradientStart ||
                (lastAppliedBackgrounds?.gradient &&
                  lastAppliedBackgrounds?.gradient?.[0]) ||
                ""
              }, ${
                gradientEnd ||
                (lastAppliedBackgrounds?.gradient &&
                  lastAppliedBackgrounds?.gradient?.[1]) ||
                ""
              })`,
            }}
          />
        </div>
      );
    } else if (tabKey?.toLowerCase() === "image") {
      return (
        <div className="PickerTabContent PickerTabContent--imagePrivew">
          <ImgCrop
            showGrid
            aspect={12} // Maintain the banner's aspect ratio
            rotate
            modalClassName="PreviewScreenCustomModal"
            beforeCrop={(file, reject) => {
              if (
                ["image/png", "image/jpeg", "image/jpg"]?.includes(file.type)
              ) {
                if (file?.size < 5 * 1024 || file?.size > 50 * 1024 * 1024) {
                  callNotification(
                    "File size exceeds the allowed limit (5KB - 50MB)",
                    "error",
                    40,
                    "personalization-preview-notification personalization-preview-mask",
                    true
                  );
                  setImageValid(false);
                  reject();
                }
              } else {
                callNotification(
                  "Unsupported file type! Please use PNG, JPG, or JPEG formats",
                  "error",
                  40,
                  "personalization-preview-notification personalization-preview-mask",
                  true
                );
                setImageValid(false);
                reject();
              }
            }}
          >
            <Upload
              name="file"
              listType="picture-card"
              showUploadList={false}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith("image/");
                if (!isImage) {
                  Modal.error({
                    title: "Upload Error",
                    content: "You can only upload image files!",
                  });
                  return Upload.LIST_IGNORE;
                }
                return true;
              }}
              onChange={(info) => {
                if (
                  info.file.status === "done" ||
                  info.file.status === "success"
                ) {
                  handleCroppedImageUpload(info.file.originFileObj);
                }
              }}
              customRequest={({ file, onSuccess }) => {
                setTimeout(() => {
                  onSuccess("ok");
                }, 0);
              }}
            >
              {croppedImage || lastAppliedBackgrounds?.image ? (
                <>
                  <img
                    src={croppedImage || lastAppliedBackgrounds?.image}
                    alt="Cropped"
                    style={{ width: "100%" }}
                  />
                </>
              ) : (
                <div>
                  <p className="ant-upload-text">
                    Click or drag image to upload
                  </p>
                </div>
              )}
            </Upload>
          </ImgCrop>
        </div>
      );
    }
    return "";
  };

  //picker output updating  -> personalization state and CSS both are updated only from this function
  const handleColorPickerOutput = (data, type, configType) => {
    if (configType === "backgroundConfig") {
      let backgroundValue = "";
      if (type === "SolidColor") {
        backgroundValue = data;
      } else if (type === "GradientColor") {
        backgroundValue = `linear-gradient(to right, ${data[0]}, ${data[1]})`;
      } else if (type === "Image") {
        backgroundValue = `${data}`;
      }
      updateVariableValueBasedOnThemeOrRoot(cssVariable, backgroundValue);
      dispatch(setComponentBackground(stateKey, backgroundValue));
    } else if (configType === "FontConfig") {
      if (type === "SolidColor") {
        updateVariableValueBasedOnThemeOrRoot(cssVariable, data);
        dispatch(setBannerTextColor(stateKey, data));
      }
    } else if (configType === "CustomTheme") {
      if (type === "SolidColor") {
        updateVariableValueBasedOnThemeOrRoot(cssVariable, data);
        dispatch(setCustomPaletteInfo(index, stateKey, data));
      }
    } else if (configType?.slice(0, -1) === "AccentColor") {
      if (type === "SolidColor") {
        // Just update the selectedAccentData but don't apply it yet
        setSelectedAccentData(data);
      }
    }
  };

  // delete banner
  const removeBannerBackground = () => {
    dispatch(setComponentBackground("CommandCenterScreenBanner", null));
    setCroppedImage(null);
    setSolidColor(null);
    setGradientEnd(null);
    setGradientStart(null);
    setLastAppliedBackgrounds({
      solid: null,
      gradient: null,
      image: null,
    });
  };

  // Helper to get display preview based on active tab
  const getPreviewStyle = () => {
    if (!hasData) {
      return {
        backgroundImage: `url(${defaultImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    }

    // For accent colors, always show the solid color
    if (configType?.slice(0, -1) === "AccentColor") {
      return {
        backgroundColor: solidColor || defaultSolidColor || "transparent",
      };
    }

    switch (activeTab) {
      case "solid":
        return {
          backgroundColor:
            solidColor || lastAppliedBackgrounds?.solid || "transparent",
        };
      case "gradient":
        const startColor =
          gradientStart ||
          (lastAppliedBackgrounds?.gradient &&
            lastAppliedBackgrounds?.gradient?.[0]);
        const endColor =
          gradientEnd ||
          (lastAppliedBackgrounds?.gradient &&
            lastAppliedBackgrounds?.gradient?.[1]);

        return {
          backgroundImage:
            startColor && endColor
              ? `linear-gradient(to right, ${startColor}, ${endColor})`
              : "none",
          backgroundSize: "cover",
        };
      case "image":
        const imageSource = croppedImage || lastAppliedBackgrounds.image;
        return {
          backgroundImage: imageSource ? `url(${imageSource})` : "none",
          backgroundSize: "cover",
        };
      default:
        return {};
    }
  };

  return (
    <div className="PreviewColorPickerInput">
      {/* There are different UI for solid color picker and custom picker */}
      {(configType === "backgroundConfig" &&
        keyComponent === "CommandCenterScreenBanner") ||
      configType?.slice(0, -1) === "AccentColor" ? (
        <div className="ColorPickerPanel">
          <div className="ColorPickerPanel__Header">
            {/* Input box for color picker  */}
            <div
              className="ColorPickerPanel__Header--selectedColor"
              style={getPreviewStyle()}
              onClick={toggleColorPicker}
            />
            {/* text that appears in the color picker  */}
            <div className="ColorPickerPanel__Header--selectedColorName">
              {!hasData
                ? "Add Fill"
                : configType?.slice(0, -1) === "AccentColor"
                ? solidColor || defaultSolidColor || "No color"
                : activeTab === "solid"
                ? solidColor || lastAppliedBackgrounds?.solid || "No color"
                : activeTab === "gradient"
                ? `${
                    gradientStart ||
                    (lastAppliedBackgrounds?.gradient &&
                      lastAppliedBackgrounds?.gradient?.[0]) ||
                    "Gradient Start"
                  } → ${
                    gradientEnd ||
                    (lastAppliedBackgrounds?.gradient &&
                      lastAppliedBackgrounds?.gradient?.[1]) ||
                    "Gradient End"
                  }`
                : activeTab === "image"
                ? croppedImage || lastAppliedBackgrounds?.image
                  ? "Image uploaded"
                  : "No image"
                : null}
            </div>
            {hasData ? (
              // condition that runs when the color picker has a default value or a selected value
              <div
                className="d-flex align-items-center"
                style={{ gap: "0.25rem" }}
              >
                <Button
                  icon={<EditOutlined />}
                  onClick={toggleColorPicker}
                  className="editBtn"
                />
                {/* Delete button - different for accent vs. banner */}
                {configType?.slice(0, -1) === "AccentColor" ? (
                  Number(configType.slice(-1)) > 3 ? (
                    <Tooltip title={"Delete color"}>
                      <Button
                        icon={<DeleteOutlined style={{ color: "white" }} />}
                        className="editBtn"
                        onClick={onDelete}
                      />
                    </Tooltip>
                  ) : null
                ) : (
                  <Button
                    icon={<DeleteOutlined style={{ color: "white" }} />}
                    className="editBtn"
                    onClick={removeBannerBackground}
                  />
                )}
              </div>
            ) : (
              // show add button if no data is present
              <Button
                icon={<PlusOutlined />}
                onClick={toggleColorPicker}
                className="editBtn"
              />
            )}
          </div>
          {/* html color picker tag renders here */}
          {isOpen && (
            <div className="ColorPickerPanel__Popover">
              <div className="ColorPickerPanel__Popover--header">
                <div className="ColorPickerPanel__Popover--headerLabel">
                  Color Picker
                </div>
                <Button
                  className="closeBtn"
                  icon={<CloseOutlined />}
                  onClick={toggleColorPicker}
                />
              </div>
              <div className="ColorPickerPanel__Popover--tabs">
                <Radio.Group
                  className="TabsButton__Group"
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                >
                  <Radio.Button
                    className={`TabsButton__Group--item ${
                      activeTab === "solid" ? "active" : ""
                    }`}
                    value="solid"
                  >
                    Solid
                  </Radio.Button>
                  {AdvancedPicker && configType !== "AccentColor" ? (
                    <>
                      <Radio.Button
                        className={`TabsButton__Group--item ${
                          activeTab === "gradient" ? "active" : ""
                        }`}
                        value="gradient"
                      >
                        Gradient
                      </Radio.Button>
                      <Radio.Button
                        className={`TabsButton__Group--item ${
                          activeTab === "image" ? "active" : ""
                        }`}
                        value="image"
                      >
                        Image
                      </Radio.Button>
                    </>
                  ) : (
                    ""
                  )}
                </Radio.Group>
              </div>
              <div className="ColorPickerPanel__Popover--tabContent mb-2">
                {getHtmlOfColorPicker(activeTab)}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="ColorPickerPanel d-flex align-items-center justify-content-space-between">
          <Input
            style={
              hasData
                ? {
                    backgroundColor: solidColor ? solidColor : defaultValue,
                  }
                : {
                    backgroundImage: `url(${defaultImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
            }
            type="color"
            value={solidColor || ""}
            onChange={(e) => handleColorChange(e.target.value)}
            className={`ColorPickerPanel__Header--selectedColor swatch-off ${
              isColorPickerDisabled ? "disabled-cursor" : ""
            }`}
            disabled={isColorPickerDisabled}
          />
          <div className="ColorPickerPanel__Header--selectedColorName">
            {hasData ? solidColor : "Add Color"}
          </div>
          <div>
            {configType.slice(0, -1) === "AccentColor" &&
            Number(configType.slice(-1)) > 3 ? (
              <Tooltip title={"Delete color"}>
                <Button
                  icon={<DeleteOutlined style={{ color: "white" }} />}
                  className="editBtn"
                  onClick={onDelete}
                />
              </Tooltip>
            ) : (
              ""
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
